import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import ChatMessage, { MessageRole } from '../models/chat_message';
import { Result } from '../models/result';
import { getPreviousMessages, streamAgentResponse, clearChatSession, getUserChats, getChatHistory, createNewSession, subscribeToChatList } from '../repositories/agentService';
import useUserStore from './userStore';
import useSchoolStore from './schoolStore';
// import useAuthStore from './authStore';
const useChatStore = create(
  persist(
    (set, get) => ({
      // State
      chatList: [], // Array of chat summary objects with sessionId, userId, lastMessage, lastTimestamp, messageCount
      currentChatMessages: [], // Array of ChatMessage objects for the currently selected chat
      isLoading: false,
      isLoadingChatHistory: false,
      isClearingChatSession: false,
      selectedChatIndex: null,
      selectedSessionId: null,
      error: null,
      chatListUnsubscribe: null, // Store the unsubscribe function

      // Actions

      /**
       * Updates the chat list with new data from Firebase subscription
       * 
       * @param {Array} newChatList - New chat list data
       */
      updateChatList: (newChatList) => {
        const { selectedSessionId } = get();
        const dataToUpdate = {
          chatList: newChatList || [],
        };

        // // Sync selectedChatIndex with selectedSessionId if needed
        // if (selectedSessionId && newChatList) {
        //   const selectedChatIndex = newChatList.findIndex(chat => chat.sessionId === selectedSessionId);
        //   if (selectedChatIndex !== -1) {
        //     dataToUpdate.selectedChatIndex = selectedChatIndex;
        //   }
        // }

        set(dataToUpdate);
      },

      /**
       * Subscribes to real-time chat list updates
       */
      subscribeToChats: () => {
        const { userProfile } = useUserStore.getState();
        const { schoolId } = useSchoolStore.getState();
        
        if (!userProfile?.userId || !schoolId) {
          console.warn('Cannot subscribe to chats: missing userId or schoolId');
          return;
        }

        // Unsubscribe from any existing subscription
        get().unsubscribeFromChats();

        try {
          const unsubscribe = subscribeToChatList(
            schoolId,
            userProfile.userId,
            (chatSessions, error) => {
              if (error) {
                console.error('Chat subscription error:', error);
                set({ error: new Result({ message: `Chat subscription error: ${error.message}`, code: "SUBSCRIPTION_ERROR" }) });
                return;
              }
              get().updateChatList(chatSessions);
            }
          );

          set({ chatListUnsubscribe: unsubscribe });
        } catch (error) {
          console.error('Failed to subscribe to chats:', error);
          set({ error: new Result({ message: `Failed to subscribe to chats: ${error.message}`, code: "SUBSCRIPTION_SETUP_ERROR" }) });
        }
      },

      /**
       * Unsubscribes from chat list updates
       */
      unsubscribeFromChats: () => {
        const { chatListUnsubscribe } = get();
        if (chatListUnsubscribe) {
          chatListUnsubscribe();
          set({ chatListUnsubscribe: null });
        }
      },

      setSelectedChatIndex: (index) => {
        const { chatList } = get();
        const sessionId = index !== null && chatList[index] ? chatList[index].sessionId : null;

        set({ 
          selectedChatIndex: index,
          selectedSessionId: sessionId,
          currentChatMessages: [] // Clear current messages when switching chats
        });
      },

      setSelectedSessionId: (sessionId) => {
        set({ selectedSessionId: sessionId });
      },



      getUserChats: async () => {
        const { userProfile } = useUserStore.getState();
        const { schoolId } = useSchoolStore.getState();
        // const { userToken } = useAuthStore.getState();
        set({ isLoading: true });
        const result = await getUserChats({ userId: userProfile.userId, schoolId: schoolId });
        // console.log(`chatList: ${JSON.stringify(result.value)}`);
        if(result.success){
          const dataToUpdate = {
            chatList: result.value, 
            isLoading: false 
          }
          //make sure the session and selectedchat index are in sync
          // every request to the chat stream returns the sessionId in its first chunk inside metadata
          // So when the chat is loaded, if the selectedSessionId is not null, then find the chatList item
          // with the same sessionId and set the selectedChatIndex to the index of that item
          if(get().selectedSessionId){
            const selectedChatIndex = dataToUpdate.chatList.findIndex(chat => chat.sessionId === get().selectedSessionId);
            if(selectedChatIndex !== -1){
              dataToUpdate.selectedChatIndex = selectedChatIndex;
            }
          }
          set(dataToUpdate);
        }else{
          set({ error: result, isLoading: false });
        }
      },

      createNewSession: async () => {
        const { userProfile } = useUserStore.getState();
        const { schoolId } = useSchoolStore.getState();
        set({ isLoading: true });
          // const { userToken } = useAuthStore.getState();
        const result = await createNewSession({ userId: userProfile.userId, schoolId: schoolId });
        if(result.success){
          set({ selectedSessionId: result.value, isLoading: false });
        }else{
          set({ error: result, isLoading: false });
        }
      },

      /**
       * Gets the current chat messages for the selected chat
       * 
       * @returns {Array<ChatMessage>} Array of ChatMessage objects
       */
      getCurrentChatMessages: () => {
        const { currentChatMessages } = get();
        return currentChatMessages;
      },

      getChatHistory: async (sessionId) => {
        const { userProfile } = useUserStore.getState();
        const { schoolId } = useSchoolStore.getState();
        // const {userToken} = useAuthStore.getState();
        set({ isLoadingChatHistory: true, error: null });
        const result = await getChatHistory({ userId: userProfile.userId, schoolId: schoolId, sessionId: sessionId });
        if(result.success){
          set({ currentChatMessages: result.value, isLoadingChatHistory: false, selectedSessionId: sessionId });
        }else{
          set({ error: result, isLoadingChatHistory: false });
        }
        return result;
      },

      /**
       * Deletes a chat from the chatList
       * 
       * @param {number} chatIndex - Index of the chat to delete
       * @returns {Result} Result object indicating success or failure
       */
      deleteChatList: async (chatIndex) => {
        try {
          const { chatList } = get();
          
          if (chatIndex < 0 || chatIndex >= chatList.length) {
            const errorResult = new Result({
              message: "Invalid chat index.",
              code: "INVALID_INDEX"
            });
            set({ error: errorResult });
            return errorResult;
          }

          

          const { userProfile, schoolId } = useUserStore.getState();
          // const { userToken } = useAuthStore.getState();
          set({ isClearingChatSession: true });
          let sessionId = chatList[chatIndex]?.sessionId;
          if(sessionId){
            var result = await clearChatSession({ userId: userProfile.userId, schoolId: schoolId, sessionId: sessionId });
          if(result.success){
         
            const newChatIndex =null;
            const newSessionId = null
            set({ selectedChatIndex: newChatIndex, selectedSessionId: newSessionId, isClearingChatSession: false, currentChatMessages: [] });
            // set({ isClearingChatSession: false });
            get().getUserChats();
          //   const newChatList = [...chatList];
          // newChatList.splice(chatIndex, 1);
           
          }else{
            set({ error: result, isClearingChatSession: false });
          }
          }else{
            set({ error: new Result({ message: "Chat not found.", code: "CHAT_NOT_FOUND" }), isClearingChatSession: false });
          }

          return new Result({
            value: true,
            message: "Chat deleted successfully."
          });
        } catch (error) {
          const errorResult = new Result({
            message: `Failed to delete chat: ${error.message}`,
            code: "DELETE_CHAT_ERROR"
          });
          set({ error: errorResult });
          return errorResult;
        }
      },

      /**
       * Adds a message to the current chat
       * 
       * @param {ChatMessage} message - Message to add
       * @returns {Result} Result object indicating success or failure
       */
      addMessageToCurrent: (message) => {
        try {
          const { currentChatMessages } = get();
          const newMessages = [...currentChatMessages, message];
          set({ currentChatMessages: newMessages });

          return new Result({
            value: message,
            message: "Message added successfully."
          });
        } catch (error) {
          const errorResult = new Result({
            message: `Failed to add message: ${error.message}`,
            code: "ADD_MESSAGE_ERROR"
          });
          set({ error: errorResult });
          return errorResult;
        }
      },

      /**
       * Updates a message in the current chat
       * 
       * @param {number} messageIndex - Index of the message
       * @param {ChatMessage} updatedMessage - Updated message
       * @returns {Result} Result object indicating success or failure
       */
      updateMessageInCurrent: (messageIndex, updatedMessage) => {
        try {
          const { currentChatMessages } = get();
          
          if (messageIndex < 0 || messageIndex >= currentChatMessages.length) {
            const errorResult = new Result({
              message: "Invalid message index.",
              code: "INVALID_INDEX"
            });
            set({ error: errorResult });
            return errorResult;
          }

          const newMessages = [...currentChatMessages];
          newMessages[messageIndex] = updatedMessage;
          set({ currentChatMessages: newMessages });

          return new Result({
            value: updatedMessage,
            message: "Message updated successfully."
          });
        } catch (error) {
          const errorResult = new Result({
            message: `Failed to update message: ${error.message}`,
            code: "UPDATE_MESSAGE_ERROR"
          });
          set({ error: errorResult });
          return errorResult;
        }
      },

      /**
       * Deletes a message from a specific chat
       * 
       * @param {number} chatIndex - Index of the chat
       * @param {number} messageIndex - Index of the message
       * @returns {Result} Result object indicating success or failure
       */
      deleteMessage: (chatIndex, messageIndex) => {
        try {
          const { chatList } = get();
          
          if (chatIndex < 0 || chatIndex >= chatList.length) {
            const errorResult = new Result({
              message: "Invalid chat index.",
              code: "INVALID_INDEX"
            });
            set({ error: errorResult });
            return errorResult;
          }

          const chat = chatList[chatIndex];
          if (messageIndex < 0 || messageIndex >= chat.length) {
            const errorResult = new Result({
              message: "Invalid message index.",
              code: "INVALID_INDEX"
            });
            set({ error: errorResult });
            return errorResult;
          }

          const newChatList = [...chatList];
          newChatList[chatIndex] = [...chat];
          newChatList[chatIndex].splice(messageIndex, 1);
          set({ chatList: newChatList });

          return new Result({
            value: true,
            message: "Message deleted successfully."
          });
        } catch (error) {
          const errorResult = new Result({
            message: `Failed to delete message: ${error.message}`,
            code: "DELETE_MESSAGE_ERROR"
          });
          set({ error: errorResult });
          return errorResult;
        }
      },



      getUserLastMessage: () => {
        try {
          const { currentChatMessages } = get();
          let userLastMessage;
          for (let i = currentChatMessages.length - 1; i >= 0; i--) {
            if (currentChatMessages[i].role === MessageRole.USER) {
              userLastMessage = currentChatMessages[i];
              break;
            }
          }
          return new Result({
            value: userLastMessage,
            message: "User last message retrieved successfully."
          });
        } catch (error) {
          return new Result({
            message: `Failed to get user last message: ${error.message}`,
            code: "GET_USER_LAST_MESSAGE_ERROR"
          });
        }
      },

      /**
       * Gets the total number of chats
       * 
       * @returns {Result} Result object with chat count
       */
      getChatCount: () => {
        try {
          const { chatList } = get();
          return new Result({
            value: chatList.length,
            message: "Chat count retrieved successfully."
          });
        } catch (error) {
          return new Result({
            message: `Failed to get chat count: ${error.message}`,
            code: "GET_COUNT_ERROR"
          });
        }
      },

      /**
       * Gets the message count for the current chat
       * 
       * @returns {number} Message count
       */
      getCurrentMessageCount: () => {
        const { currentChatMessages } = get();
        return currentChatMessages.length;
      },

      /**
       * Clears all chats
       * 
       * @returns {Result} Result object indicating success or failure
       */
      clearAllChats: () => {
        try {
          set({ chatList: [] });
          return new Result({
            value: true,
            message: "All chats cleared successfully."
          });
        } catch (error) {
          const errorResult = new Result({
            message: `Failed to clear chats: ${error.message}`,
            code: "CLEAR_CHATS_ERROR"
          });
          set({ error: errorResult });
          return errorResult;
        }
      },

      /**
       * Clears the current chat messages
       * 
       * @returns {Result} Result object indicating success or failure
       */
      clearCurrentChatMessages: async () => {
        try {
          const { userProfile } = useUserStore.getState();
          const { schoolId } = useSchoolStore.getState();
          set({ currentChatMessages: [], isClearingChatSession: true });
          const { selectedSessionId } = get();
          // const { userToken } = useAuthStore.getState();
          var result = await clearChatSession({ userId: userProfile.userId, schoolId: schoolId, sessionId: selectedSessionId });
          if(result.success){
            set({ isClearingChatSession: false });
          }else{
            set({ error: result, isClearingChatSession: false });
          }

          return new Result({
            value: true,
            message: "Chat messages cleared successfully."
          });
        } catch (error) {
          const errorResult = new Result({
            message: `Failed to clear chat messages: ${error.message}`,
            code: "CLEAR_CHAT_MESSAGES_ERROR"
          });
          set({ error: errorResult });
          return errorResult;
        }
      },

      /**
       * Clears any errors
       */
      clearError: () => {
        set({ error: null });
        return new Result({ value: true });
      },

      /**
       * Gets the current error
       * 
       * @returns {Result|null} The current error Result object or null
       */
      getError: () => {
        const { error } = get();
        return error;
      },

      /**
       * Sends a message and handles the streaming response from the agent.
       *
       * @param {object} params - The parameters for the agent service.
       * @param {object} params.schoolInfo - School information.
       * @param {string} params.prompt - The user's message.
       * @param {object} params.userInfo - User information.
       * @param {boolean} params.noCache - Whether to use cache.
       * @returns {Promise<Result>} A result indicating the initial outcome. The streaming updates happen in the background.
       */
      sendStreamingMessage: async (params) => {
        set({ isLoading: true, error: null });
        const { schoolInfo, prompt, userInfo, noCache } = params;
        // const { userToken } = useAuthStore.getState();
        const { currentUser } = useUserStore.getState();
        const roleToken = currentUser?.roleToken;
        try {
          if(!roleToken){
            throw new Error('Failed to validate the user role.');
          }
          const { currentChatMessages, selectedSessionId } = get();
          let isNewChat = selectedSessionId === null;
          // console.log(`isNewChat: ${isNewChat} sessionId: ${selectedSessionId}`)
          // Get previous messages for the API call before adding the new user message.
          const previousMessages = getPreviousMessages(currentChatMessages);

          // Add user message to the store. If [isAgentSwitch] is true, then don't add the user message to the store.
          const userMessage = new ChatMessage({ role: MessageRole.USER, content: prompt });
          if(!params.isAgentSwitch){
            get().addMessageToCurrent(userMessage);
          }

          // Add a placeholder for the assistant's response.
          const assistantMessage = new ChatMessage({ role: MessageRole.ASSISTANT, content: '', isStreaming: true });
          get().addMessageToCurrent(assistantMessage);
          const assistantMessageIndex = get().currentChatMessages.length - 1;

          // This callback will be invoked by the streaming service with new data.
          const onMessageCallback = (payload) => {
            const currentMessages = get().currentChatMessages;
            const currentAssistantMessage = currentMessages[assistantMessageIndex];
            const { type, data, isDone, isError, messageId } = payload;
            let newContent;

            
            if (type === 'string') {
              newContent = currentAssistantMessage.content + data;
            } else if (type === 'json') {
              try {
                // The data is a JSON string. Parse and stringify it.
                const prettyJson = JSON.stringify(JSON.parse(data), null, 2);
                newContent = `${currentAssistantMessage.content}\n\`\`\`json\n${prettyJson}\n\`\`\`\n`;
              } catch (e) {
                // Should not happen if service-side validation is correct, but as a fallback:
                newContent = currentAssistantMessage.content + data;
              }
            } else if(type === 'metadata') {
               set({ selectedSessionId: data.sessionId, selectedChatIndex: 0 });
               return;
            } else {
              // Ignore unknown types
              return;
            }

           if(!isDone){
            const updatedMessage = new ChatMessage({
              ...currentAssistantMessage,
              isStreaming: false,
              content: newContent,
              isError: isError || false,
              id: messageId
            });

            // if(isDone && isNewChat){
            //   get().getUserChats();
            // }
            get().updateMessageInCurrent(assistantMessageIndex, updatedMessage);
           }else if(isError){
            const error = data??'An error occurred while getting the response from the agent.';
            const currentMessages = get().currentChatMessages;
            const currentAssistantMessage = currentMessages[assistantMessageIndex];
            const updatedMessage = new ChatMessage({
                ...currentAssistantMessage,
                isStreaming: false,
                content: currentAssistantMessage.content.length > 0 ? currentAssistantMessage.content : error,
                isError: true,
                id: messageId
              });

              get().updateMessageInCurrent(assistantMessageIndex, updatedMessage);
            set({ error: new Result({ message: `Failed to get stream response: ${error}`, code: "STREAM_ERROR" }), isLoading: false });
           }
          };

          // Start the streaming call. This will not block.
          let responseResult = await streamAgentResponse(
            { schoolInfo, prompt, previousMessages, userInfo, noCache, isAgentSwitch: params.isAgentSwitch, sessionId: selectedSessionId,
              roleToken: roleToken
             },
            onMessageCallback
          )
          set({ isLoading: false });

          // if(!responseResult.success){
           
          // }

          return new Result({ value: { sessionId: selectedSessionId }, message: "Message sent and streaming started." });

        } catch (error) {
          const result = new Result({ message: `Failed to send message: ${error.message}`, code: "SEND_MESSAGE_ERROR", });
          set({ error: result, isLoading: false });
          return result;
        }
      }
    }),
    {
      name: 'chat-storage',
      partialize: (state) => ({
        // Note: We don't persist currentChatMessages, error, or loading states as they should be fresh on each session
      })
    }
  )
);

export default useChatStore;
